// react
import { useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Autocomplete, Box, Chip, TextField } from '@mui/material';

// shared components
import SensorTag from '@/components/ui/SensorTag';
import UnitTag from '@/components/ui/UnitTag';

// relative
import type { ReadUnitDto } from '../types';

interface UnitAutocompleteProps {
  units: ReadUnitDto[];
  loading: boolean;
  value: ReadUnitDto[];
  onChange: (newValue: ReadUnitDto[]) => void;
}

const UnitAutocomplete = ({
  units,
  loading,
  value,
  onChange,
}: UnitAutocompleteProps) => {
  const { t } = useTranslation('view-data');
  const [inputValue, setInputValue] = useState('');
  return (
    <Autocomplete
      id="unit-autocomplete"
      sx={{ width: '100%' }}
      loading={loading}
      multiple
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        // ignore programmatic resets from value reference changes (e.g. live mode refetch)
        if (reason !== 'reset') setInputValue(newInputValue);
      }}
      onBlur={() => setInputValue('')}
      onClose={() => setInputValue('')}
      options={units}
      getOptionLabel={(unit) => `${unit.Name} (${unit.UnitId})` || ''}
      filterOptions={(options, { inputValue }) => {
        const search = inputValue.trim().toLowerCase();
        return options.filter((unit) => {
          const nameMatch = unit.Name?.toLowerCase().includes(search);
          const tagMatch = unit.Tags?.some((tag) =>
            tag.Text?.toLowerCase().includes(search),
          );
          const sensorMatch = unit.UnitSensors?.some((s) =>
            String(s.Sensor?.SensorId).includes(search),
          );
          const unitIdMatch = String(unit.UnitId).includes(search);
          return nameMatch || tagMatch || sensorMatch || unitIdMatch;
        });
      }}
      value={value || []}
      onChange={(event, newValue) => onChange(newValue)}
      renderOption={(props, unit) => {
        const tagTexts = unit.Tags
          ? Array.from(new Set(unit.Tags.map((t) => t.Text)))
          : [];
        const sensorIds = unit.UnitSensors
          ? Array.from(
              new Set(
                unit.UnitSensors.map((s) => s.Sensor?.SensorId).filter(Boolean),
              ),
            )
          : [];
        return (
          <li {...props} key={props.key}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Chip label={`${unit.Name} (${unit.UnitId})`} size="small" />
              {tagTexts.map((tag) => (
                <UnitTag key={tag} label={tag} />
              ))}
              {sensorIds.map((sid) => (
                <SensorTag key={sid} label={sid} />
              ))}
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('units')}
          placeholder={t('search')}
          size="small"
        />
      )}
    />
  );
};

export default UnitAutocomplete;
